<template>
  <div v-if="status" class="ProductCardStoreStatus flex items-center gap-6 px-4 py-2 mt-4 leading-single type-sm">
    <div
      class="w-8 h-8 inline-block rounded-full shrink-0"
      :class="status.near ? 'bg-bg-successStrong' : 'border border-border-warningStrong'"
    />
    <span v-if="status.distanceKm !== undefined">
      {{ $t('productList.product.store.distance', { distance: formatKm(status.distanceKm) }) }}
    </span>
    <span v-else-if="status.near">{{ $t('productList.product.store.available') }}</span>
    <span v-else>{{ $t('productList.product.store.none') }}</span>
  </div>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import { useGlobalContentStore } from '~/store/globalContent';
import { useLocationStore } from '~/store/location';
import useDisplaySamples from '~/composables/useDisplaySamples';
import useRetailerLocations from '~/composables/useRetailerLocations';
import { DISPLAY_RADIUS_KM, getDisplayStores } from '~/util/displayStores';

const props = defineProps<{
  product: ProductModel,
}>();

const globalContent = useGlobalContentStore();
const locationStore = useLocationStore();
const { retailers, load: loadRetailers } = useRetailerLocations();
const { samples, request } = useDisplaySamples();

// The card only loads one variant, but the product counts as on display if any of its variants is
const partNos = computed(() => (props.product.partNumbers?.length ? props.product.partNumbers : [props.product.partNo]));

const displayStoreIds = computed(() => {
  const lists = partNos.value.map(partNo => samples.value[partNo]);
  if (lists.some(list => list === undefined)) {
    return undefined;
  }
  if (lists.every(list => list === null)) {
    return null;
  }
  return [...new Set(lists.flatMap(list => list ?? []))];
});

onMounted(() => {
  locationStore.load();
  partNos.value.forEach(request);
});

watch([() => locationStore.location, displayStoreIds], ([location, ids]) => {
  if (location && ids?.length) {
    loadRetailers();
  }
}, { immediate: true });

const formatKm = (km: number) => new Intl.NumberFormat(globalContent.getLocale, { maximumFractionDigits: 1 }).format(km);

/**
 * near: green dot (display store available, within DISPLAY_RADIUS_KM when we know where the user is)
 * distanceKm: only set when we know where the user is
 */
const status = computed(() => {
  const ids = displayStoreIds.value;
  if (!ids) {
    return null;
  }
  if (!ids.length) {
    return { near: false };
  }
  const location = locationStore.location;
  if (!location) {
    return { near: true };
  }
  if (!retailers.value) {
    return null;
  }
  const nearest = getDisplayStores(ids, retailers.value, location)[0];
  if (!nearest) {
    return { near: false };
  }
  const distanceKm = nearest.distanceKm;
  return { near: distanceKm <= DISPLAY_RADIUS_KM, distanceKm };
});
</script>
